import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Card,
  Empty,
  ErrorNote,
  Eyebrow,
  Field,
  Modal,
  PageHead,
  Spinner,
  Status,
  Table,
} from '../../components/ui'
import { api, wsPath } from '../../lib/api'
import { fmtDate, fmtMoney } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { useWs } from './useWs'

interface Batch {
  id: string
  status: string
  totalMinor: number
  currency: string
  test: boolean
  createdAt: string
  approvedAt: string | null
  createdBy: string
}
interface Line {
  id: string
  kind: 'payout' | 'verification'
  batch_id: string
  status: string
  provider: string
  amount_minor: string
  adjustment_minor: string
  currency: string
  provider_ref: string | null
  error: string | null
  partner_name: string
  partner_email: string
  method: string | null
  commissions: number
}

export function PayoutsPage() {
  const { t, has, locale } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [manual, setManual] = useState<Line | null>(null)
  const q = useQuery({
    queryKey: ['payouts', ws],
    queryFn: () =>
      api<{
        batches: Batch[]
        lines: Line[]
        minPayoutMinor: number
        currency: string
        dualApprovalThresholdMinor: number
        role: string
        payoutsPaused: boolean
        payoutsPausedReason: string | null
      }>(wsPath(ws, '/payouts')),
    refetchInterval: 15_000,
  })
  const inv = () => qc.invalidateQueries()
  const draft = useMutation({
    mutationFn: (test: boolean) =>
      api<{ batch: { id: string } | null; draft: { skipped: { reason: string }[] } }>(
        wsPath(ws, '/payouts/draft'),
        { body: { test } },
      ),
    onSuccess: (r) => {
      if (!r.batch)
        setNotice(
          [
            t('noPayable'),
            ...new Set(
              r.draft.skipped.map((s) => {
                const k = `skip.${s.reason}`
                return has(k) ? t(k) : s.reason
              }),
            ),
          ].join(' '),
        )
      else setNotice(null)
      inv()
    },
  })
  const approve = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/payouts/${id}/approve`), { method: 'POST' }),
    onSuccess: inv,
  })
  const cancel = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/payouts/${id}/cancel`), { method: 'POST' }),
    onSuccess: inv,
  })
  if (q.isLoading || !q.data) return <Spinner />
  const { batches, lines, role } = q.data
  const canDraft = role !== 'viewer'
  const canApprove = role === 'owner' || role === 'finance'
  return (
    <div>
      <PageHead
        title={t('payouts')}
        actions={
          canDraft && (
            <>
              <button
                type="button"
                className="btn"
                onClick={() => draft.mutate(true)}
                disabled={draft.isPending}
              >
                {t('draftTestBatch')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => draft.mutate(false)}
                disabled={draft.isPending}
              >
                {t('draftBatch')}
              </button>
            </>
          )
        }
      />
      <p className="-mt-4 mb-6 text-sm text-mute">
        {t('minPayout')}: {fmtMoney(q.data.minPayoutMinor, q.data.currency, locale)} ·{' '}
        {t('dualApproval', { amount: fmtMoney(q.data.dualApprovalThresholdMinor, q.data.currency, locale) })}
      </p>
      {q.data.payoutsPaused && (
        <div role="status" className="card card-pad mb-4 border-sunset/50 text-sm text-sunset">
          {q.data.payoutsPausedReason === 'billing_overdue'
            ? t('payoutsPausedBilling')
            : t('payoutsPausedManual')}
        </div>
      )}
      {notice && <div className="card card-pad mb-4 text-sm text-body">{notice}</div>}
      <ErrorNote error={draft.error ?? approve.error ?? cancel.error} />
      {!batches.length ? (
        <Empty title={t('noPayable')} text={t('emptyCommissionsText')} />
      ) : (
        <div className="grid gap-4">
          {batches.map((b) => {
            const bl = lines.filter((l) => l.batch_id === b.id)
            const byProvider = bl.reduce<Record<string, number>>((acc, l) => {
              acc[l.provider] = (acc[l.provider] ?? 0) + Number(l.amount_minor)
              return acc
            }, {})
            return (
              <Card key={b.id} pad={false}>
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-hairline px-6 py-4">
                  <div>
                    <Eyebrow className="mb-1">
                      {t('batch')} · {fmtDate(b.createdAt, locale, true)}{' '}
                      {b.test && <span className="badge badge-warn ml-2">test</span>}
                    </Eyebrow>
                    <div className="flex items-center gap-3">
                      <span className="num text-2xl">{fmtMoney(b.totalMinor, b.currency, locale)}</span>
                      <Status value={b.status} />
                    </div>
                    <div className="mono mt-1 text-xs text-mute">
                      {Object.entries(byProvider)
                        .map(([p, v]) => `${p}: ${fmtMoney(v, b.currency, locale)}`)
                        .join(' · ')}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {b.status === 'draft' && canApprove && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => approve.mutate(b.id)}
                        disabled={approve.isPending}
                      >
                        {t('approveBatch')}
                      </button>
                    )}
                    {b.status === 'draft' && canDraft && (
                      <button type="button" className="btn" onClick={() => cancel.mutate(b.id)}>
                        {t('cancelBatch')}
                      </button>
                    )}
                    {canApprove && bl.some((l) => l.provider === 'crypto_manual') && (
                      <a className="btn" href={wsPath(ws, `/payouts/${b.id}/export.csv`)}>
                        {t('exportCsv')}
                      </a>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('partner')}</th>
                        <th>{t('method')}</th>
                        <th>{t('amount')}</th>
                        <th>{t('status')}</th>
                        <th>Ref</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {bl.map((l) => (
                        <tr key={l.id}>
                          <td>
                            <div className="text-ink">{l.partner_name}</div>
                            <div className="text-xs text-mute">
                              {l.partner_email} · {l.commissions ?? 0} {t('commissions').toLowerCase()}
                            </div>
                          </td>
                          <td className="text-xs">
                            {l.method ?? l.provider}
                            {l.kind === 'verification' && (
                              <div className="mt-1">
                                <span className="badge badge-info">{t('testTransfer')}</span>
                              </div>
                            )}
                          </td>
                          <td className="mono text-ink">
                            {fmtMoney(l.amount_minor, l.currency, locale)}
                            {Number(l.adjustment_minor) < 0 && (
                              <div className="text-[11px] text-danger">
                                {fmtMoney(l.adjustment_minor, l.currency, locale)} debt
                              </div>
                            )}
                          </td>
                          <td>
                            <Status value={l.status} />
                            {l.error && (
                              <div className="mono mt-1 text-[11px] text-danger">{l.error.slice(0, 80)}</div>
                            )}
                          </td>
                          <td className="mono max-w-[160px] truncate text-xs">{l.provider_ref ?? '—'}</td>
                          <td className="text-right">
                            {canApprove &&
                              l.status === 'sent' &&
                              ['crypto_manual', 'manual'].includes(l.provider) && (
                                <button type="button" className="btn btn-sm" onClick={() => setManual(l)}>
                                  {t('markPaid')}
                                </button>
                              )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )
          })}
        </div>
      )}
      {manual && <MarkPaid line={manual} onClose={() => setManual(null)} />}
    </div>
  )
}

function MarkPaid({ line, onClose }: { line: Line; onClose: () => void }) {
  const { t, locale } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const [ref, setRef] = useState('')
  const m = useMutation({
    mutationFn: () => api(wsPath(ws, `/payouts/item/${line.id}/mark-paid`), { body: { reference: ref } }),
    onSuccess: () => {
      qc.invalidateQueries()
      onClose()
    },
  })
  return (
    <Modal open onClose={onClose} title={t('markPaid')}>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          m.mutate()
        }}
      >
        <div className="text-sm text-body">
          {line.partner_name} · {fmtMoney(line.amount_minor, line.currency, locale)} · {line.method}
        </div>
        <Field label={t('txReference')}>
          <input className="input mono" required value={ref} onChange={(e) => setRef(e.target.value)} />
        </Field>
        <button type="submit" className="btn btn-primary" disabled={m.isPending}>
          {t('markPaid')}
        </button>
        <ErrorNote error={m.error} />
      </form>
    </Modal>
  )
}

export function FraudPage() {
  const { t, locale } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['fraud', ws],
    queryFn: () =>
      api<{
        flags: {
          id: string
          rule: string
          evidence: Record<string, unknown>
          status: string
          created_at: string
          partner_name: string
          partner_email: string
          amount_minor: string | null
          currency: string | null
        }[]
      }>(wsPath(ws, '/fraud')),
  })
  const resolve = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: string }) =>
      api(wsPath(ws, `/fraud/${id}/resolve`), { body: { decision } }),
    onSuccess: () => qc.invalidateQueries(),
  })
  if (q.isLoading) return <Spinner />
  const flags = q.data?.flags ?? []
  return (
    <div>
      <PageHead title={t('fraud')} />
      {!flags.length ? (
        <Empty title={t('fraud')} text={t('noFlags')} />
      ) : (
        <Table head={[t('partner'), t('rule'), t('evidence'), t('amount'), t('status'), '']}>
          {flags.map((f) => (
            <tr key={f.id}>
              <td>
                <div className="text-ink">{f.partner_name}</div>
                <div className="text-xs text-mute">{f.partner_email}</div>
              </td>
              <td className="mono text-xs">{f.rule}</td>
              <td
                className="mono max-w-[180px] truncate text-xs text-mute"
                title={JSON.stringify(f.evidence)}
              >
                {Object.values(f.evidence).join(' · ')}
              </td>
              <td className="mono">
                {f.amount_minor ? fmtMoney(f.amount_minor, f.currency ?? 'USD', locale) : '—'}
              </td>
              <td>
                <Status value={f.status} />
                <div className="mono mt-1 text-[11px] text-mute">{fmtDate(f.created_at, locale)}</div>
              </td>
              <td className="text-right">
                {f.status === 'open' && (
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => resolve.mutate({ id: f.id, decision: 'approve' })}
                    >
                      {t('approve')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => resolve.mutate({ id: f.id, decision: 'reject' })}
                    >
                      {t('reject')}
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  )
}
